
import { Star } from "lucide-react";
import React from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Table, TableBody } from "@/components/ui/table";
import RatingRow from "@/components/RatingRow";

interface StarIconProps {
  rating: number;
  starPosition: number;
  iconSize: number;
  onClick?: () => void;
  className?: string;
}

const StarIcon = ({ rating, starPosition, iconSize, onClick, className = "" }: StarIconProps) => {
  // Cap rating to max 5 stars
  const cappedRating = Math.min(rating, 5);
  
  // Calculate star color based on rating
  const getStarColor = () => {
    if (rating >= 6) return "text-green-500 fill-green-500";         // Keep rating 6 as green
    if (rating <= 1) return "text-red-500 fill-red-500";             // Keep rating 1 as red
    if (rating <= 2) return "text-orange-500 fill-orange-500";       // Orange for rating 2
    if (rating <= 3) return "text-yellow-600 fill-yellow-600";       // Bright yellow for rating 3
    if (rating <= 4) return "text-yellow-500 fill-yellow-500";       // Medium yellow for rating 4
    return "text-yellow-300 fill-yellow-300";                        // Light yellow for rating 5
  };
  
  const starColor = getStarColor();
  
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  // Custom SVG for half star
  const HalfStar = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={starColor}
        aria-label="Half a star"
      >
        {/* Left half (filled) */}
        <path
          d="M12 17.8 5.8 21 7 14.1 2 9.3l7-1L12 2"
          fill="currentColor"
          stroke="none"
        />
        {/* Right half (outlined) */}
        <path
          d="M12 2v15.8 M12 17.8 18.2 21 17 14.1 22 9.3l-7-1L12 2"
          fill="transparent"
          className="stroke-current"
        />
      </svg>
    );
  };
  
  // Render star with tooltip/hover card
  const renderStarWithHoverCard = (starComponent: React.ReactNode) => (
    <HoverCard openDelay={0}>
      <HoverCardTrigger asChild>
        <span onClick={handleClick} className={`${className} cursor-help`}>
          {starComponent}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="z-[200] w-auto">
        <Table className="w-full">
          <TableBody>
            <RatingRow rating={1} description="Not recommended - Worse than stock" />
            <RatingRow rating={2} description="Stock or equivalent to stock" />
            <RatingRow rating={3} description="Better than stock" />
            <RatingRow rating={4} description="Significant upgrade over stock" />
            <RatingRow rating={5} description="Top tier features and quality" />
            <RatingRow rating={5} description="Recommended - best all-round upgrade" isGreen={true} />
          </TableBody>
        </Table>
      </HoverCardContent>
    </HoverCard>
  );
  
  if (starPosition <= Math.floor(cappedRating)) {
    return renderStarWithHoverCard(
      <Star 
        size={iconSize} 
        className={`${starColor}`} 
        aria-label={`${starPosition} out of 5 stars`}
      />
    );
  } else if (starPosition - 0.5 <= cappedRating) {
    return renderStarWithHoverCard(<HalfStar />);
  } else {
    return renderStarWithHoverCard(
      <Star 
        size={iconSize} 
        className="text-gray-400 dark:text-gray-500 stroke-gray-400 dark:stroke-gray-500 fill-transparent" 
        aria-label="Empty star"
      />
    );
  }
};

export default StarIcon;
